/**
 * RiskyChargeConfirmModal — hard confirmation for coach-triggered charges,
 * especially ones carrying a big discount.
 *
 * Sachin 2026-03-06: "I just saw a charge go through that had a 100%
 * discount. Can you check what happened? Maybe I did something wrong?"
 * → Root cause was a native `window.confirm` prompt that made an easily-
 * missed one-liner ("💸 DISCOUNT: 100% off forever") the ONLY thing
 * standing between a legit customer and a $0 forever charge. Replacing
 * with a real modal that:
 *
 *   1. Loud red visuals when effective price = $0
 *   2. Bold "FOREVER" callout when duration = forever
 *   3. Explicit "silent block-coupon fallback" flag when the discount
 *      came from block config vs coach-typed input
 *   4. Type-to-confirm gating:
 *        - Effective $0            → type "COMP"    to enable Confirm
 *        - Duration = forever      → type "FOREVER" to enable Confirm
 *        - Both above              → type BOTH tokens ("COMP FOREVER")
 *        - Discount ≥ 50% (not $0) → type "DISCOUNT" to enable Confirm
 *        - No discount / low disc  → just click Confirm
 *
 * Contract:
 *   props = {
 *     open: bool,
 *     onClose: () => void,
 *     onConfirm: () => Promise<void> | void,
 *     title: str,
 *     athleteName: str,
 *     lines: [{ label: str, sub?: str, amountCents: int, strike?: bool }],
 *     basePriceCents: int | null,        // e.g. sticker sub price
 *     effectivePriceCents: int | null,   // after discount, per period / one-shot
 *     interval?: "month" | "week" | "year" | null,
 *     discount?: {
 *       kind: "percent" | "fixed",
 *       value: number,                   // percent (0-100) or dollars
 *       durationLabel: str,              // "once" | "forever" | "for 6 months"
 *       durationKind: "once" | "repeating" | "forever" | "one_time",
 *       source: "ad_hoc" | "block_coupon",
 *       reason?: str,
 *     } | null,
 *     footNote?: str,                    // e.g. "First charge fires NOW"
 *     busy?: bool,
 *   }
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ShieldAlert, X, Loader2, Sparkles } from "lucide-react";

const fmt = (cents) => {
    if (cents == null) return "—";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
        .format((cents || 0) / 100);
};

const intervalShort = (i) => (i === "week" ? "wk" : i === "year" ? "yr" : i === "month" ? "mo" : "");

export default function RiskyChargeConfirmModal({
    open,
    onClose,
    onConfirm,
    title,
    athleteName,
    lines = [],
    basePriceCents,
    effectivePriceCents,
    interval,
    discount,
    footNote,
    busy,
}) {
    const [typed, setTyped] = useState("");

    useEffect(() => {
        if (open) setTyped("");
    }, [open]);

    // Risk classification. Each triggered flag adds a required token to the
    // confirm string. All tokens must be present (order-agnostic) to enable
    // the Confirm button.
    const risk = useMemo(() => {
        const flags = { free: false, forever: false, big: false };
        const tokens = [];
        // ANY $0 charge requires typing COMP — including a custom comp pack
        // whose base was $0 from the start (not just a 100%-discounted one).
        if (effectivePriceCents === 0) {
            flags.free = true;
            tokens.push("COMP");
        }
        if (discount?.durationKind === "forever") {
            flags.forever = true;
            tokens.push("FOREVER");
        }
        // "Big" only kicks in when neither of the above did (so the coach
        // isn't asked to type 3 tokens for what's really a comp-forever).
        if (!flags.free && !flags.forever && discount) {
            const pct = discount.kind === "percent"
                ? Number(discount.value) || 0
                : basePriceCents && basePriceCents > 0
                    ? ((Number(discount.value) || 0) * 100) / basePriceCents * 100 // dollars → % of base
                    : 0;
            if (pct >= 50) {
                flags.big = true;
                tokens.push("DISCOUNT");
            }
        }
        return { flags, tokens };
    }, [effectivePriceCents, basePriceCents, discount]);

    if (!open) return null;

    const needsType = risk.tokens.length > 0;
    const requiredPhrase = risk.tokens.join(" ");
    const enteredTokens = typed.trim().toUpperCase().split(/\s+/).filter(Boolean);
    const requiredTokens = risk.tokens.slice();
    const typedMatches =
        !needsType ||
        (enteredTokens.length === requiredTokens.length &&
            requiredTokens.every((t) => enteredTokens.includes(t)));

    const isFree = effectivePriceCents === 0;
    const isForever = discount?.durationKind === "forever";

    return (
        <div
            data-testid="risky-charge-confirm-modal"
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            role="dialog"
            aria-modal="true"
        >
            <div className="relative bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-lg my-8 overflow-hidden shadow-2xl">
                {/* Top stripe — red when free, amber when forever, brand otherwise */}
                <div
                    className={`h-1 ${
                        isFree
                            ? "bg-red-600"
                            : isForever
                                ? "bg-amber-500"
                                : "bg-brand-red"
                    }`}
                    aria-hidden="true"
                />

                {/* Header */}
                <div className="px-5 sm:px-6 pt-5 pb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div
                            className={`text-[10px] tracking-[0.3em] uppercase mb-1.5 inline-flex items-center gap-2 ${
                                isFree ? "text-red-400" : "text-brand-red"
                            }`}
                        >
                            {isFree ? <ShieldAlert className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                            Confirm charge
                        </div>
                        <h2 className="font-display text-xl sm:text-2xl tracking-wider text-white leading-tight break-words">
                            {title}
                        </h2>
                        {athleteName && (
                            <p className="text-xs text-zinc-400 mt-1">
                                For <span className="text-white">{athleteName}</span>
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        data-testid="risky-charge-close"
                        onClick={onClose}
                        disabled={busy}
                        className="text-zinc-400 hover:text-white p-1 -m-1 flex-shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 sm:px-6 pb-5 space-y-4">
                    {/* Line items */}
                    {lines.length > 0 && (
                        <div className="border border-zinc-800 rounded-lg overflow-hidden">
                            {lines.map((l, i) => (
                                <div
                                    key={i}
                                    className={`px-3 py-2.5 flex items-start justify-between gap-3 text-sm ${
                                        i > 0 ? "border-t border-zinc-900" : ""
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className={`text-zinc-100 ${l.strike ? "line-through text-zinc-500" : ""}`}>
                                            {l.label}
                                        </div>
                                        {l.sub && (
                                            <div className="text-[11px] text-zinc-500 mt-0.5">{l.sub}</div>
                                        )}
                                    </div>
                                    <div
                                        className={`font-mono whitespace-nowrap ${
                                            l.strike ? "line-through text-zinc-500" : "text-white"
                                        }`}
                                    >
                                        {fmt(l.amountCents)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Discount summary — only when a discount exists */}
                    {discount && (
                        <div
                            data-testid="risky-charge-discount-summary"
                            className={`rounded-lg border p-3 ${
                                isFree
                                    ? "border-red-700/50 bg-red-950/30"
                                    : isForever
                                        ? "border-amber-600/50 bg-amber-950/20"
                                        : "border-zinc-800 bg-zinc-900/40"
                            }`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle
                                    className={`w-4 h-4 flex-shrink-0 ${
                                        isFree ? "text-red-400" : isForever ? "text-amber-400" : "text-zinc-400"
                                    }`}
                                />
                                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 font-semibold">
                                    Discount applied
                                </div>
                                {discount.source === "block_coupon" && (
                                    <span
                                        data-testid="risky-charge-source-block"
                                        className="ml-auto text-[9px] tracking-[0.15em] uppercase border border-amber-500/50 text-amber-300 px-1.5 py-0.5 rounded"
                                    >
                                        From block · auto-applied
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-white">
                                <span className="font-mono">
                                    {discount.kind === "percent"
                                        ? `${discount.value}% off`
                                        : `${fmt(Math.round((Number(discount.value) || 0) * 100))} off`}
                                </span>
                                {" "}
                                <span
                                    className={`text-xs ${
                                        isForever ? "text-amber-300 font-bold uppercase tracking-wider" : "text-zinc-400"
                                    }`}
                                >
                                    {discount.durationLabel}
                                </span>
                            </div>
                            {discount.reason && (
                                <div className="text-[11px] text-zinc-400 mt-1.5">
                                    Reason: <span className="text-zinc-200">{discount.reason}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Total / effective price hero */}
                    <div
                        className={`rounded-lg border p-4 ${
                            isFree
                                ? "border-red-600 bg-red-950/40"
                                : "border-zinc-800 bg-zinc-900/50"
                        }`}
                    >
                        <div className="flex items-baseline justify-between gap-3">
                            <div>
                                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">
                                    {interval ? "You will charge" : "Total charged now"}
                                </div>
                                {basePriceCents != null && basePriceCents !== effectivePriceCents && (
                                    <div className="text-xs text-zinc-500 line-through font-mono">
                                        was {fmt(basePriceCents)}{interval ? `/${intervalShort(interval)}` : ""}
                                    </div>
                                )}
                            </div>
                            <div className="text-right">
                                <div
                                    data-testid="risky-charge-effective-price"
                                    className={`font-display text-3xl tracking-wider ${
                                        isFree ? "text-red-400" : "text-white"
                                    }`}
                                >
                                    {fmt(effectivePriceCents)}
                                    {interval && (
                                        <span className="text-sm font-mono text-zinc-400 ml-1">
                                            /{intervalShort(interval)}
                                        </span>
                                    )}
                                </div>
                                {isFree && (
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-red-400 mt-1">
                                        Zero dollars · full comp
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Foot note (e.g. "First charge fires NOW") */}
                    {footNote && (
                        <p className="text-[11px] text-zinc-400 leading-relaxed">
                            {footNote}
                        </p>
                    )}

                    {/* Type-to-confirm gate */}
                    {needsType && (
                        <div
                            data-testid="risky-charge-typeconfirm"
                            className="border border-zinc-800 rounded-lg p-3 bg-zinc-900/60"
                        >
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mb-2">
                                Type{" "}
                                <span className="font-mono text-white bg-zinc-950 border border-zinc-700 px-1.5 py-0.5 rounded">
                                    {requiredPhrase}
                                </span>{" "}
                                to enable Confirm
                            </div>
                            <input
                                type="text"
                                data-testid="risky-charge-typeconfirm-input"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                                autoFocus
                                placeholder={requiredPhrase}
                                spellCheck="false"
                                autoCapitalize="characters"
                                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-white focus:outline-none focus:border-red-500 tracking-wider"
                            />
                            <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
                                This safeguard exists because a $0 or forever discount can silently drain revenue. Say the words on purpose.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 sm:px-6 py-4 border-t border-zinc-800 bg-black/40 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <button
                        type="button"
                        data-testid="risky-charge-cancel"
                        onClick={onClose}
                        disabled={busy}
                        className="text-xs tracking-[0.2em] uppercase px-4 py-2.5 border border-zinc-700 text-zinc-200 hover:bg-zinc-900 transition-colors rounded disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="risky-charge-confirm"
                        onClick={onConfirm}
                        disabled={busy || !typedMatches}
                        className={`text-xs tracking-[0.2em] uppercase px-4 py-2.5 inline-flex items-center justify-center gap-2 rounded transition-colors ${
                            !typedMatches
                                ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                                : isFree
                                    ? "bg-red-600 hover:bg-red-700 text-white"
                                    : "bg-brand-red hover:bg-red-700 text-white"
                        }`}
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isFree ? "Confirm $0 charge" : "Confirm & charge"}
                    </button>
                </div>
            </div>
        </div>
    );
}
